/**
 * Widget runtime contract: postMessage protocol between host (player/app) and widget iframe.
 * All messages use a versioned envelope. Host sends INIT, CONFIG_UPDATE, FRAME_ENTER, FRAME_EXIT;
 * widget sends READY, ERROR; optional STATE_* request/response for state API.
 */

export const WIDGET_PROTOCOL_VERSION = 1

/** Origin that sends messages (host = player or app). */
export const WIDGET_PROTOCOL_SOURCE_HOST = 'lucimark-player'

/** Origin that sends messages (widget iframe). */
export const WIDGET_PROTOCOL_SOURCE_WIDGET = 'lucimark-widget'

/** Host → widget message types */
export const WIDGET_MSG_INIT = 'INIT'
export const WIDGET_MSG_CONFIG_UPDATE = 'CONFIG_UPDATE'
export const WIDGET_MSG_FRAME_ENTER = 'FRAME_ENTER'
export const WIDGET_MSG_FRAME_EXIT = 'FRAME_EXIT'
export const WIDGET_MSG_TIME_TICK = 'TIME_TICK'

/** Widget → host message types */
export const WIDGET_MSG_READY = 'READY'
export const WIDGET_MSG_ERROR = 'ERROR'
export const WIDGET_MSG_METRIC = 'METRIC'

/** State API: widget → host request */
export const WIDGET_MSG_STATE_GET = 'STATE_GET'
export const WIDGET_MSG_STATE_SET = 'STATE_SET'
export const WIDGET_MSG_STATE_CLEAR = 'STATE_CLEAR'

/** Host → widget state API response */
export const WIDGET_MSG_STATE_RESPONSE = 'STATE_RESPONSE'

export type WidgetMessageTypeHostToWidget
  = | typeof WIDGET_MSG_INIT
    | typeof WIDGET_MSG_CONFIG_UPDATE
    | typeof WIDGET_MSG_FRAME_ENTER
    | typeof WIDGET_MSG_FRAME_EXIT
    | typeof WIDGET_MSG_TIME_TICK
    | typeof WIDGET_MSG_STATE_RESPONSE

export type WidgetMessageTypeWidgetToHost
  = | typeof WIDGET_MSG_READY
    | typeof WIDGET_MSG_ERROR
    | typeof WIDGET_MSG_METRIC
    | typeof WIDGET_MSG_STATE_GET
    | typeof WIDGET_MSG_STATE_SET
    | typeof WIDGET_MSG_STATE_CLEAR

/** Envelope for all protocol messages */
export interface IWidgetMessageEnvelope {
  source: typeof WIDGET_PROTOCOL_SOURCE_HOST | typeof WIDGET_PROTOCOL_SOURCE_WIDGET
  version: number
  type: string
  requestId?: string
  payload?: unknown
}

/** INIT payload: sent once after the widget sends READY */
export interface IWidgetInitPayload {
  config: Record<string, unknown>
}

/** CONFIG_UPDATE payload: merged config (e.g. after campaign/playlist/frame merge) */
export interface IWidgetConfigUpdatePayload {
  config: Record<string, unknown>
}

/** READY payload: widget confirms it received INIT and is ready */
export interface IWidgetReadyPayload {
  /** Optional widget name / version for logging */
  name?: string
  version?: string
}

/** ERROR payload: widget reports an error */
export interface IWidgetErrorPayload {
  message: string
  code?: string
}

/** State API scope */
export type TWidgetStateScope = 'widget' | 'playlist' | 'instance' | 'campaign'

/** STATE_GET / STATE_SET / STATE_CLEAR request payload (widget → host) */
export interface IWidgetStateRequestPayload {
  requestId: string
  scope: TWidgetStateScope
  key?: string
  value?: unknown
}

/** STATE_RESPONSE payload (host → widget) */
export interface IWidgetStateResponsePayload {
  requestId: string
  success: boolean
  value?: unknown
  error?: string
}

/** Build a host → widget envelope */
export function createHostToWidgetMessage(
  type: WidgetMessageTypeHostToWidget,
  payload?: unknown
): IWidgetMessageEnvelope {
  return {
    source: WIDGET_PROTOCOL_SOURCE_HOST,
    version: WIDGET_PROTOCOL_VERSION,
    type,
    payload
  }
}

/** Build a widget → host envelope */
export function createWidgetToHostMessage(
  type: WidgetMessageTypeWidgetToHost,
  payload?: unknown,
  requestId?: string
): IWidgetMessageEnvelope {
  return {
    source: WIDGET_PROTOCOL_SOURCE_WIDGET,
    version: WIDGET_PROTOCOL_VERSION,
    type,
    requestId,
    payload
  }
}

/** Check if a message event is from the widget protocol (by envelope shape) */
export function isWidgetProtocolMessage(data: unknown): data is IWidgetMessageEnvelope {
  if (data == null || typeof data !== 'object') { return false }
  const d = data as Record<string, unknown>
  return (
    (d.source === WIDGET_PROTOCOL_SOURCE_HOST || d.source === WIDGET_PROTOCOL_SOURCE_WIDGET)
    && typeof d.version === 'number'
    && typeof d.type === 'string'
  )
}
